/**
 * Admin: Recent user posts across all collections with moderation actions
 * (edit / archive / delete / restore / notify-user). Uses the generic
 * /admin/moderate/{content_type}/{id} endpoint. Every action is written
 * to /admin/audit-log for review.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Archive, RotateCcw, Trash2, Send, Filter, History } from "lucide-react";

const TYPES = [
  { v: "", label: "All" },
  { v: "causes", label: "Causes" },
  { v: "appeals", label: "Appeals" },
  { v: "help_offers", label: "Help offers" },
  { v: "news_submissions", label: "News" },
  { v: "memorial_nominations", label: "Memorial noms" },
  { v: "family_trees", label: "Trees" },
  { v: "family_members", label: "Members" },
  { v: "family_events", label: "Family events" },
  { v: "family_albums", label: "Albums" },
  { v: "family_comments", label: "Comments" },
  { v: "sp_articles", label: "Articles" },
  { v: "events", label: "Events" },
];

export default function AdminModeration() {
  const [items, setItems] = useState([]);
  const [audit, setAudit] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [showAudit, setShowAudit] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [rec, aud] = await Promise.all([
        http.get("/admin/recent-content", { params: { limit_per_type: 50 } }),
        http.get("/admin/audit-log", { params: { limit: 100 } }),
      ]);
      setItems(rec.data.items);
      setAudit(aud.data.items);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = typeFilter ? items.filter((i) => i.content_type === typeFilter) : items;

  const moderate = async (item, action) => {
    const messages = {
      delete: "Delete this content permanently. Add a note the user will receive?",
      archive: "Archive this item. Add a note the user will receive?",
      restore: "Restore this item. Add a note?",
      notify: "Send a note to the user (no content change). What's the message?",
    };
    const note = window.prompt(messages[action] || "Note (optional)?") || null;
    if (action === "delete" && !window.confirm("Really delete permanently?")) return;
    const notify = !!note; // Always notify the user when there's a note
    try {
      await http.post(`/admin/moderate/${item.content_type}/${item.id}`, { action, note, notify });
      toast.success(`${action} done${notify ? " + user notified" : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-moderation">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="overline mb-1">DAILY MONITORING</div>
          <h2 className="font-display text-2xl">Recent user posts</h2>
          <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            Review, edit, archive, delete, or notify authors across every content type. Every action is
            recorded in the audit log.
          </p>
        </div>
        <button
          onClick={() => setShowAudit((v) => !v)}
          data-testid="am-audit-toggle"
          className="wofy-btn-ghost text-xs"
        >
          <History className="w-3.5 h-3.5" /> {showAudit ? "Hide" : "Show"} audit log
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <Filter className="w-4 h-4 opacity-60" />
        {TYPES.map((t) => (
          <button
            key={t.v || "all"}
            onClick={() => setTypeFilter(t.v)}
            data-testid={`am-filter-${t.v || "all"}`}
            className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border"
            style={{
              borderColor: typeFilter === t.v ? "var(--wofy-primary)" : "var(--wofy-border)",
              background: typeFilter === t.v ? "var(--wofy-primary)" : "transparent",
              color: typeFilter === t.v ? "white" : "var(--wofy-text-soft)",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {showAudit && (
        <section className="wofy-card mb-6" data-testid="am-audit-panel">
          <div className="overline mb-2">AUDIT LOG</div>
          {audit.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Nothing logged yet.</p>
          ) : (
            <ul className="divide-y text-xs" style={{ borderColor: "var(--wofy-line)" }}>
              {audit.map((a) => (
                <li key={a.id} className="py-2">
                  <div>
                    <b>{a.actor_role}</b> · <b>{a.action}</b> on {a.content_type}
                    {" · "}
                    <span style={{ color: "var(--wofy-text-soft)" }}>{new Date(a.created_at).toLocaleString()}</span>
                    {a.extra?.notified && <span className="ml-2 text-[10px] uppercase font-mono tracking-wider" style={{ color: "#2C7A4B" }}>NOTIFIED</span>}
                  </div>
                  {a.note && <div className="italic mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>&ldquo;{a.note}&rdquo;</div>}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {loading ? (
        <p className="text-sm">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="wofy-card text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>No content in this view.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((it) => {
            const archived = it.status === "archived";
            return (
              <div key={`${it.content_type}-${it.id}`} data-testid={`am-item-${it.id}`} className="wofy-card flex items-start gap-3" style={archived ? { opacity: 0.55 } : undefined}>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: "var(--wofy-primary)" }}>
                    {it.content_type}
                    {archived && <span className="ml-2" style={{ color: "#B23A3A" }}>ARCHIVED</span>}
                  </div>
                  <div className="font-semibold">{it.title || "(untitled)"}</div>
                  <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                    {it.created_at ? new Date(it.created_at).toLocaleString() : ""}
                    {it.owner_name && ` · ${it.owner_name}`}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0 flex-wrap">
                  {archived ? (
                    <button onClick={() => moderate(it, "restore")} data-testid={`am-restore-${it.id}`} className="wofy-btn-ghost text-xs" title="Restore">
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button onClick={() => moderate(it, "archive")} data-testid={`am-archive-${it.id}`} className="wofy-btn-ghost text-xs" title="Archive">
                      <Archive className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => moderate(it, "notify")} data-testid={`am-notify-${it.id}`} className="wofy-btn-ghost text-xs" title="Notify user">
                    <Send className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => moderate(it, "delete")} data-testid={`am-delete-${it.id}`} className="wofy-btn-ghost text-xs" title="Delete" style={{ color: "#B23A3A" }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
